import { ApolloProvider } from '@apollo/client';
import {
  ModalProvider,
  ToastContextProvider,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import AuthContextProvider from '../context/AuthContext';
import LexContextProvider from '../context/LexContext';
import { useApollo } from '../lib/apolloClient';
import { useAttribution } from '../lib/attribution';
import { useGtm } from '../lib/gtm';
import { init as initNProgress } from '../lib/nprogress';
import phog from '../utils/analytics/posthog/posthog';
import { usePageView } from '../utils/analytics/usePageView';
import { createClient as createSupabaseClient } from '../utils/supabase/component';

/* eslint-disable import/order */
import 'nprogress/nprogress.css'; //styles of nprogress
import '../styles/globals.css';
import Script from 'next/script';
/* eslint-enable import/order */


interface BrowserCookie {
  name?: string;
  domain?: string;
}

export default function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  /* eslint-disable */
  const deleteWWWCookies = async () => {
    try {
      // @ts-ignore
      const cookies = await cookieStore.getAll();
      // @ts-ignore
      const cookiesToRemove = cookies.filter(
        (cook: BrowserCookie) =>
          (cook.name?.includes('sb-') &&
            cook?.domain?.includes('www.joinleland.com')) ||
          cook.name?.includes('appSession'),
      );

      for (const cook of cookiesToRemove) {
        // @ts-ignore
        await cookieStore.delete(cook);
      }
    } catch (e) {
      console.error('Error deleting www cookies:', e);
    }
  };
  /* eslint-enable */
  // this is another one time fix. In the case the users have a bad cookie (.www) and not hitting rate limits, they can't login unless we kill that cookie
  useEffect(() => {
    void (async () => {
      await deleteWWWCookies();
    })();
  }, []);

  /* eslint-disable */
  const deleteCookies = async () => {
    try {
      // @ts-ignore
      const cookies = await cookieStore.getAll();
      // @ts-ignore
      const cookiesToRemove = cookies.filter((cook) =>
        cook.name?.includes('sb-'),
      );

      for (const cook of cookiesToRemove) {
        // @ts-ignore
        await cookieStore.delete(cook);
      }
    } catch (e) {
      console.error('Error deleting cookies:', e);
    }
  };
  /* eslint-enable */

  useEffect(() => {
    // @ts-expect-error `lelandVersion` does not exist on `window`
    window.lelandVersion = process.env.NEXT_PUBLIC_LELAND_VERSION;
  }, []);

  const apolloClient = useApollo(pageProps);

  useGtm();

  useAttribution();

  usePageView();

  useEffect(() => {
    initNProgress();
    void phog.init();
  }, []);

  // this is a one time client fix for duplicate cookies. We should remove this ASAP after August 2nd, 2025
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = createSupabaseClient();
        const { data, error } = await supabase.auth.refreshSession();
        console.log('checking session', data, error);
        if (error?.message.includes('rate limit')) {
          console.log('kill the rate limit cookies');
          await deleteCookies();
          console.log('cookies killed');
          return;
        }
      } catch (e) {
        console.error('Error refreshing session', e);
      }
    };
    void setTimeout(() => {
      void checkSession();
    }, 5000);
  }, []);

  const router = useRouter();

  // Use layout defined at page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <ApolloProvider client={apolloClient}>
      <AuthContextProvider>
        <LexContextProvider>
          <ToastContextProvider>
            <ModalProvider pathname={router.asPath}>
              <Script src="https://r.wdfl.co/rw.js" data-rewardful="ae076f" />
              <Script id="rewardful-queue" strategy="beforeInteractive">
                {`(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`}
              </Script>
              {getLayout(<Component {...pageProps} />, pageProps)}
            </ModalProvider>
          </ToastContextProvider>
        </LexContextProvider>
      </AuthContextProvider>
    </ApolloProvider>
  );
}
